/** @format */

import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  _$,
  Button,
  Copy,
  Dialog,
  Pagination,
  Table,
  TableColumn,
  TreeSelect
} from '@king-design/react'
import { DataItem } from '@king-design/react/components/tree/useNodes'
import { TriggerConditionType } from '../../container'
import {
  SearchHistoryEventFields,
  SearchHistoryEventFieldsOps,
  TriggerRuleKeyEnum
} from './container'
import QuickRangeDate, { DateTimeProps } from 'src/components/QuickRangeDate'
import dayjs from 'dayjs'
import { CountContext } from '../..'
import { EventItemProps } from 'src/pages/EventCenter/constants'
import { useAsync } from 'react-use'
import { getHistoryEvent } from 'src/api/ApiEventOpsTrigger'
import { PageProps, initPage } from 'src/utils/interface'
import useGlobalData from 'src/hooks/useGlobalData'
import { RegionStateType } from 'src/store/globalDataSlice'
import { isNumber, isString } from 'lodash'

interface EventHistoryProps {
  className?: string
  visible: boolean
  onClose: () => void
  conditions: TriggerConditionType[][]
}

const ruleToFilter = (rule: TriggerConditionType) => {
  const { Key, Operator, Value } = rule
  let val = isNumber(Value) ? Value.toString() : Value
  if (Operator === 'in' && isString(Value)) {
    val = (Value as string).split(',')
  }
  return {
    [SearchHistoryEventFields[Key]]: {
      Op: SearchHistoryEventFieldsOps[Operator],
      Value: val
    }
  }
}

const EventHistory: React.FC<EventHistoryProps> = ({ className, visible, onClose, conditions }) => {
  const context = useContext(CountContext)
  const eventResult = context.eventResult || []
  // Region
  const regionResult = useGlobalData('monitorRegionList') as RegionStateType['list']
  const [dateTime, setDateTime] = useState<DateTimeProps>({
    StartTime: dayjs().subtract(1, 'h').valueOf(),
    EndTime: dayjs().valueOf()
  })
  const [rules, setRules] = useState<string[]>([])
  const [page, setPage] = useState<PageProps>(initPage)
  const [selectData, setSelectData] = useState<DataItem<string>[]>([])

  // 请求事件历史列表
  const { value: eventHistoryList } = useAsync(async () => {
    if (visible && dateTime?.StartTime && dateTime.EndTime && rules?.length) {
      const ruleParams = []
      rules.forEach(i => {
        const index = Number(i)
        if (isFinite(index) && !isNaN(index)) {
          ruleParams.push(
            conditions[index]
              .filter(item => SearchHistoryEventFields[item.Key])
              .reduce((res, item) => {
                return { ...res, ...ruleToFilter(item) }
              }, {})
          )
        } else {
          const itemSplit = i.split('～～')
          const index = Number(itemSplit[0])
          ruleParams[index] = {
            ...(ruleParams[index] || {}),
            ...ruleToFilter({
              Key: itemSplit[1] as keyof typeof SearchHistoryEventFields,
              Operator: itemSplit[2],
              Value: itemSplit[3] || ''
            })
          }
        }
      })
      const params = {
        StartTime: dateTime.StartTime,
        EndTime: dateTime.EndTime,
        Filters: ruleParams,
        PageIndex: page.Page,
        PageSize: page.PageSize
      }
      const result = await getHistoryEvent(params)
      return {
        data: result?.EventFormatData || [],
        total: result?.TotalCount || 0
      }
    }
  }, [visible, dateTime, rules, conditions, page])

  useEffect(() => {
    if (visible && conditions?.length) {
      const filterData = conditions.map((condition, index) => {
        const filterCondition = condition.filter(item => {
          return SearchHistoryEventFields[item.Key]
        })
        const filterProduct: EventItemProps[] = eventResult.filter(
          event => event.productType === condition[0].Value
        )
        return {
          label: `#${index + 1}.`,
          key: index + '',
          children: filterCondition.map(item => {
            let findValue = item.Value
            if (item.Key === 'namespaceid') {
              findValue = filterProduct?.[0]?.productName
            } else if (item.Key === 'event_type') {
              findValue = (item.Value as string[])
                ?.map(value => {
                  return filterProduct.find(event => event.eventType === value)?.eventTypeDesc
                })
                .join('，')
            } else if (item.Key === 'event_name') {
              findValue = Array.isArray(item.Value)
                ? (item.Value as string[])
                    ?.map(value => {
                      return filterProduct.find(event => event.name === value)?.nameDesc
                    })
                    .join('，')
                : filterProduct.find(event => event.name === item.Value)?.nameDesc
            } else if (item.Key === 'event_level') {
              findValue = (item.Value as string[])
                ?.map(value => {
                  return filterProduct.find(event => event.eventLevel === value)?.eventLevel
                })
                .join('，')
            } else if (item.Key === 'event_status') {
              findValue = (item.Value as string[])
                ?.map(value => {
                  return filterProduct.find(event => event.eventStatus === value)?.eventStatus
                })
                .join('，')
            } else if (item.Key === 'region_key') {
              findValue = (item.Value as string[])
                ?.map(value => {
                  return regionResult.find(region => region.regionKey === value)?.regionName
                })
                .join('、')
            }
            return {
              label: `${TriggerRuleKeyEnum[item.Key]} ${item.Operator} ${findValue || ''}`,
              key: `${index}～～${item.Key}～～${item.Operator}～～${item.Value || ''}`
            }
          })
        }
      }, [])
      console.log('filterData', filterData)
      setSelectData(filterData)
      setRules(filterData.map(item => item.key))
    } else {
      setSelectData([])
      setRules([])
      setPage(initPage)
      setDateTime({
        StartTime: dayjs().subtract(1, 'h').valueOf(),
        EndTime: dayjs().valueOf()
      })
    }
  }, [visible, conditions])

  return (
    <div id="event-history-dialog">
      <Dialog
        value={visible}
        width={1000}
        title={_$('历史事件')}
        container={() => document.querySelector(`#rule-drawer`)}
        onChangeValue={show => {
          if (!show) {
            onClose()
          }
        }}
        slotFooter={<Button onClick={onClose}>{_$('关闭')}</Button>}
      >
        <div className={className}>
          <div className="header">
            <QuickRangeDate
              format="YYYY-MM-DD HH:mm"
              optionalDateRange={30}
              // eslint-disable-next-line no-restricted-syntax -- QuickRangeDate 与组件约定的默认范围
              defautlValue={'近1小时'}
              // eslint-disable-next-line no-restricted-syntax -- QuickRangeDate 与组件约定的默认范围
              quickRange={['近1小时', '近1天', '近3天', '近1周']}
              changeValue={date => {
                setDateTime({
                  StartTime: date[0] ? dayjs(date[0]).valueOf() : undefined,
                  EndTime: date[1] ? dayjs(date[1]).valueOf() : undefined
                })
                setPage({
                  ...page,
                  Page: 1
                })
              }}
            />
            <div className="header_popover">
              <TreeSelect
                checkbox
                clearable
                nowrap
                data={selectData}
                value={rules}
                defaultExpandAll={false}
                container={() => document.querySelector(`.header_popover`)}
                filterable
                onChangeValue={value => {
                  setRules(value)
                  setPage({
                    ...page,
                    Page: 1
                  })
                }}
              />
            </div>
          </div>
          <div className="content">
            <Table
              data={eventHistoryList?.data || []}
              // animation={false}
              rowKey={record => record.timestamp}
              checkType="none"
            >
              <TableColumn
                key="namespaceid"
                title={_$('产品类型')}
                width="80"
                fixed="left"
                slotTemplate={([{ alarmObjInfo }]) => {
                  const filterProduct: EventItemProps = eventResult.find(
                    event => event.productType === alarmObjInfo.namespaceid
                  )
                  return filterProduct?.productName || '-'
                }}
              />
              <TableColumn
                key="instanceid"
                title={_$('关联实例')}
                width="120"
                fixed="left"
                slotTemplate={([{ alarmObjInfo }]) => {
                  return (
                    <ul style={{ margin: 0 }}>
                      <li>{alarmObjInfo.instance_name}</li>
                      <li style={{ color: '#999' }}>{alarmObjInfo.instance_id}</li>
                    </ul>
                  )
                }}
              />
              <TableColumn
                key="region"
                title={_$('所属区域')}
                width="80"
                slotTemplate={([{ alarmObjInfo }]) => {
                  const findRegion = regionResult.find(
                    region => region.regionKey === alarmObjInfo.regionKey
                  )
                  return findRegion?.regionName || '-'
                }}
              />
              <TableColumn
                key="event_title"
                title={_$('事件名称')}
                width="80"
                slotTemplate={([{ alarmObjInfo }]) => {
                  return alarmObjInfo?.event_title || '-'
                }}
              />
              <TableColumn
                key="event_type"
                title={_$('事件类型')}
                width="80"
                slotTemplate={([{ alarmObjInfo }]) => {
                  const findEvent = eventResult.find(
                    event => event.eventType === alarmObjInfo.event_type
                  )
                  return findEvent?.eventTypeDesc || '-'
                }}
              />
              <TableColumn
                key="event_level"
                title={_$('事件等级')}
                width="60"
                slotTemplate={([{ alarmObjInfo }]) => {
                  return alarmObjInfo?.event_level || '-'
                }}
              />
              <TableColumn
                key="event_status"
                title={_$('事件状态')}
                width="60"
                slotTemplate={([{ alarmObjInfo }]) => {
                  return alarmObjInfo?.event_status || '-'
                }}
              />
              <TableColumn
                key="content"
                title={_$('事件内容')}
                width="150"
                slotTemplate={([{ alarmObjInfo }]) => {
                  return alarmObjInfo?.content || '-'
                }}
              />
              <TableColumn
                key="operations"
                title={_$('操作')}
                fixed="right"
                width="60"
                slotTemplate={([record]) => {
                  const jsonString = JSON.stringify(record)
                  return (
                    <Copy text={jsonString}>
                      <a>{_$('复制')}</a>
                    </Copy>
                  )
                }}
              />
            </Table>
            <Pagination
              value={page.Page}
              limit={page.PageSize}
              size="small"
              total={eventHistoryList?.total || 0}
              onChangeValue={value => {
                setPage({
                  ...page,
                  Page: value
                })
              }}
              onChangeLimit={limit => {
                setPage({
                  ...page,
                  PageSize: limit
                })
              }}
            />
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default styled(EventHistory)`
  .k-select-values {
  }
  .k-dropdown-menu {
    max-width: 300px;
    max-height: 300px;
    .k-tree-label {
      align-items: flex-start;
      line-height: 16px;
      padding-top: 4px;
      padding-bottom: 4px;
      .k-tree-text {
        max-width: 100%;
        word-break: break-all;
        white-space: normal;
      }
    }
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .header_right {
      display: flex;
      align-items: center;
      line-height: 20px;
      .k-icon {
        margin-right: 4px;
      }
    }
  }
  .content {
    min-height: 300px;
    margin-top: 16px;
  }
`
